import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardBody, CardHeader, Container,
  Row, Col, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, Label
} from "reactstrap";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import axios from "../../../../utills/api";
import { toast } from "react-toastify";
import CountUp from "react-countup";
import { io } from "socket.io-client";


// ---------------- COMPONENT ----------------
const Dashboard = () => {

  const [bookings, setBookings] = useState([]);
  const [team, setTeam] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [services, setServices] = useState([]);
  const [socket, setSocket] = useState(null);
  const [now, setNow] = useState(Date.now());


  // ---------------- FETCH ----------------
  useEffect(() => {
    fetchBookings();
    fetchTeam();
  }, []);

  // 🔥 SOCKET CONNECT
  useEffect(() => {
    const s = io("http://localhost:5000", {
      transports: ["websocket"],
    });

    setSocket(s);

    return () => s.disconnect();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const tenantId = localStorage.getItem("tenantId");
    socket.emit("join", tenantId);

  }, [socket]);

  useEffect(() => {
    if (!socket) return;

    // 🟢 NEW BOOKING
    socket.on("spa:new", (data) => {
      setBookings(prev => [data, ...prev]);
    });

    // 🟡 UPDATE BOOKING
    socket.on("spa:update", (updated) => {
      setBookings(prev =>
        prev.map(b => (b._id === updated._id ? updated : b))
      );
    });

    return () => {
      socket.off("spa:new");
      socket.off("spa:update");
    };

  }, [socket]);


  const fetchBookings = async () => {
    try {
      const res = await axios.get("/spa");
      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Booking fetch error");
    }
  };

  const fetchTeam = async () => {
    try {
      const res = await axios.get("/team");

      const filtered = res.data.filter(
        (t) =>
          t.department?.toLowerCase().includes("spa") &&
          t.status?.toLowerCase() === "active"
      );

      setTeam(filtered);
    } catch {
      toast.error("Team fetch error");
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);



  // ---------------- KPI ----------------
  const today = new Date().toISOString().split("T")[0];

  const todayBookings = bookings.filter(b => b.date === today).length;
  const pending = bookings.filter(b => b.status === "pending").length;
  const assigned = bookings.filter(b => b.assignedTo).length;
  const revenue = bookings.reduce((sum, b) => sum + (b.price || 0), 0);

  // ---------------- SEARCH FILTER ----------------
  const filteredBookings = bookings.filter((b) =>
    b.name?.toLowerCase().includes(search.toLowerCase()) ||
    b.roomNumber?.toString().includes(search)
  );

  const [form, setForm] = useState({
    serviceId: "",
    treatmentName: "",
    price: "",
    duration: "",
    name: "",
    roomNumber: "",
    mobileNumber: "",
    date: "",
    time: "",
    therapist: "no-pref",
    addons: {
      steam: false,
      jacuzzi: false,
      herbalTea: false,
      aromaticOils: false
    },
    notes: ""
  });

  const fetchServices = async () => {
    try {
      const res = await axios.get("/spa-services");
      setServices(res.data || []);
    } catch {
      toast.error("Service fetch failed");
    }
  };

  const handleServiceChange = (id) => {
    const selected = services.find(s => s._id === id);

    if (!selected) return;

    setForm(prev => ({
      ...prev,
      serviceId: id,
      treatmentName: selected.name,
      price: selected.price,
      duration: 60 // if you don’t have duration in API
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddonChange = (e) => {
    const { name, checked } = e.target;

    setForm(prev => ({
      ...prev,
      addons: {
        ...prev.addons,
        [name]: checked
      }
    }));
  };

 

  const getAutoStatus = (b) => {
  const start = new Date(`${b.date} ${b.time}`).getTime();
  const duration = 60; // or b.duration if you add later
  const end = start + duration * 60000;

  if (now >= end) return "completed";
  if (now >= start) return "ongoing";
  return "pending";
};

const getBestTherapist = () => {
  if (!team.length) return "";

  const workload = team.map(member => {
    const count = bookings.filter(
      b => b.assignedTo === member.name
    ).length;

    return { name: member.name, count };
  });

  // sort by least work
  workload.sort((a, b) => a.count - b.count);

  return workload[0]?.name || "";
};

const isSlotAvailable = () => {
  return !bookings.some(b => {
    if (b.assignedTo !== form.assignedTo) return false;

    return (
      b.date === form.date &&
      b.time === form.time
    );
  });
};

const handleSubmit = async () => {
  try {

    const autoAssign = getBestTherapist();

    // ✅ VALIDATION FIRST
    if (!isSlotAvailable(autoAssign)) {
      toast.error(`${autoAssign} already has a booking at this time`);
      return;
    }

    await axios.post("/spa/admin-create", {
      name: form.name,
      mobileNumber: form.mobileNumber,
      roomNumber: form.roomNumber,
      treatmentName: form.treatmentName,
      price: form.price,
      date: form.date,
      time: form.time,
      therapist: form.therapist,
      addons: form.addons,
      notes: form.notes,
      assignedTo: autoAssign
    });

    toast.success(`Assigned to ${autoAssign}`);
    setModal(false);

  } catch {
    toast.error("Booking failed");
  }
};

  // ---------------- TABLE COLUMNS ----------------
  const spaColumns = useMemo(() => [

    { header: "Guest", accessorKey: "name" },
    {
      header: "Mobile",
      accessorKey: "mobile",
      cell: ({ getValue }) => getValue() || "-"
    },
    { header: "Room", accessorKey: "roomNumber" },
    { header: "Service", accessorKey: "service" },
    { header: "Date", accessorKey: "date" },
    { header: "Time", accessorKey: "time" },
    { header: "Preference", accessorKey: "preference" },

    // ✅ ADDONS
    {
      header: "Addons",
      accessorKey: "addons",
      cell: ({ row }) => {
        const addons = row.original.addons || {};
        const selected = Object.keys(addons).filter(k => addons[k]);
        return selected.length ? selected.join(", ") : "None";
      }
    },

    { header: "Note", accessorKey: "note" },
    { header: "Price", accessorKey: "price" },

    // ✅ STATUS
    {
      header: "Status",
      accessorKey: "status",
     cell: ({ row }) => {
  const autoStatus = getAutoStatus(row.original);

  let cls = "secondary";
  if (autoStatus === "pending") cls = "warning";
  if (autoStatus === "ongoing") cls = "primary";
  if (autoStatus === "completed") cls = "success";

  return <span className={`badge bg-${cls}`}>{autoStatus}</span>;
}
    },

    // ✅ ASSIGN
    {
      header: "Assign",
      cell: ({ row }) => {
        const booking = row.original;

        return (
          <select
            className="form-select form-select-sm"
            value={booking.assignedTo || ""}
            onChange={async (e) => {
              try {
                await axios.patch("/spa/assign", {
                  bookingId: booking._id,
                  assignedTo: e.target.value
                });

                setBookings(prev =>
                  prev.map(b =>
                    b._id === booking._id
                      ? { ...b, assignedTo: e.target.value }
                      : b
                  )
                );

                toast.success("Assigned!");
              } catch {
                toast.error("Assign failed");
              }
            }}
          >
            <option value="">Assign</option>
            {team.map(t => (
              <option key={t._id} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        );
      }
    }

  ], [team]);

  // ---------------- UPCOMING ----------------
  const upcoming = bookings
    .filter(b => new Date(`${b.date} ${b.time}`) > new Date())
    .slice(0, 5);

  const getLiveCountdown = (date, time) => {
    const target = new Date(`${date} ${time}`).getTime();
    const diff = target - now;

    if (diff <= 0) return "Started";

    const hrs = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff / (1000 * 60)) % 60);
    const secs = Math.floor((diff / 1000) % 60);

    return `${hrs}h ${mins}m ${secs}s`;
  };

  // ---------------- TEAM TASK PROGRESS ----------------
  const teamProgress = useMemo(() => {
    if (!team.length) return [];

    const totalTasks = bookings.length || 1;

    return team.map((member, index) => {
      const assignedTasks = bookings.filter(
        b => b.assignedTo === member.name
      ).length;

      const percent = Math.round((assignedTasks / totalTasks) * 100);

      return {
        name: member.name,
        count: assignedTasks,
        percent,
        color: ["success", "primary", "warning", "info", "danger"][index % 5]
      };
    });
  }, [team, bookings]);


  // ---------------- UI ----------------
  return (
    <div className="page-content">
      <Container fluid>

        <BreadCrumb title="Spa Dashboard" pageTitle="Spa & Boutique" />

        {/* 🔥 TOP BAR */}
        <Card>
          <CardBody className="d-flex justify-content-between align-items-center">
            <Input
              placeholder="Search bookings..."
              style={{ maxWidth: "300px" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div>
              <Button
                color="primary"
                className="me-2"
                onClick={() => {
                  setModal(true);
                  fetchServices();
                }}
              >
                + Create Booking
              </Button>
              <Button color="success">
                + Add Service
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* 🔥 KPI */}
        <Row className="mt-3 d-flex flex-nowrap">

          {[
            {
              label: "Today",
              counter: todayBookings,
              icon: "bx bx-calendar-check",
              color: "#007bff",
              bgColor: "rgba(0, 123, 255, 0.1)",
            },
            // {
            //   label: "Pending",
            //   counter: pending,
            //   icon: "bx bx-time-five",
            //   color: "#ffc107",
            //   bgColor: "rgba(255, 193, 7, 0.15)",
            // },
            // {
            //   label: "Assigned",
            //   counter: assigned,
            //   icon: "bx bx-user-check",
            //   color: "#28a745",
            //   bgColor: "rgba(40, 167, 69, 0.1)",
            // },
            {
              label: "Revenue",
              counter: revenue,
              prefix: "₹",
              icon: "bx bx-rupee",
              color: "#dc3545",
              bgColor: "rgba(220, 53, 69, 0.1)",
            },
            {
              label: "Services",
              counter: bookings.length,
              icon: "bx bx-spa",
              color: "#6f42c1",
              bgColor: "rgba(111, 66, 193, 0.1)",
            },
            {
              label: "All Bookings",
              counter: bookings.length,
              icon: "bx bx-list-ul",
              color: "#20c997",
              bgColor: "rgba(32, 201, 151, 0.12)",
            },
          ].map((item, i) => (
            <Col xl={3} md={4} sm={6} key={i}>
              <Card className="border-0 bg-white">
                <CardBody className="d-flex align-items-center justify-content-between">

                  <div>
                    <p className="text-uppercase fw-semibold text-muted mb-1">
                      {item.label}
                    </p>

                    <h4 className="fs-22 fw-bold text-dark mb-2">
                      {item.prefix || ""}
                      <CountUp
                        start={0}
                        end={item.counter}
                        duration={1.2}
                        separator=","
                      />
                    </h4>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: item.bgColor,
                      borderRadius: "0.5rem",
                      width: "48px",
                      height: "48px",
                    }}
                  >
                    <i
                      className={item.icon}
                      style={{ color: item.color, fontSize: "1.5rem" }}
                    />
                  </div>

                </CardBody>
              </Card>
            </Col>
          ))}

        </Row>

        {/* 🔥 TABLE */}
        <Row className="mt-3">

          {/* 🔥 TABLE (8 COL) */}
          <Col md="8">
            <Card>
              <CardHeader>All Bookings</CardHeader>
              <CardBody>
                <div className="table-responsive" style={{ whiteSpace: "nowrap" }}>


                  <TableContainer
                    columns={spaColumns}
                    data={filteredBookings}
                    customPageSize={5}
                    isPagination
                  />
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* 🔥 TEAM TASK PROGRESS (4 COL) */}
          <Col md="4">
            <Card>
              <CardHeader>Team Task Progress</CardHeader>
              <CardBody>

                {teamProgress.length === 0 ? (
                  <p>No team data</p>
                ) : (
                  teamProgress.map((t, i) => (
                    <div key={i} className="mb-3">

                      {/* NAME + COUNT */}
                      <div className="d-flex justify-content-between">
                        <strong>{t.name}</strong>
                        <span>{t.count} ({t.percent}%)</span>
                      </div>

                      {/* PROGRESS BAR */}
                      <div className="progress mt-1" style={{ height: "8px" }}>
                        <div
                          className={`progress-bar bg-${t.color}`}
                          style={{ width: `${t.percent}%` }}
                        />
                      </div>

                    </div>
                  ))
                )}

              </CardBody>
            </Card>
          </Col>

        </Row>

        {/* 🔥 LIVE + UPCOMING */}
        <Row className="mt-3">

          {/* LIVE */}
          <Col md="6">
            <Card>
              <CardHeader>Live Activity</CardHeader>
              <CardBody style={{ maxHeight: 300, overflowY: "auto" }}>
                {bookings.slice(0, 10).map((b, i) => (
                  <div
                    key={i}
                    className="border p-2 mb-2"
                    style={{
                      backgroundColor: b.assignedTo
                        ? "rgba(40, 167, 69, 0.1)"   // light green
                        : "rgba(220, 53, 69, 0.1)"   // light red
                    }}
                  >
                    🔔 {b.name} → {b.assignedTo || "Not Assigned"}
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>

          {/* UPCOMING */}
          <Col md="6">
            <Card>
              <CardHeader>Upcoming Services</CardHeader>
              <CardBody>
                {upcoming.length === 0 ? (
                  <p>No upcoming bookings</p>
                ) : (
                  upcoming.map((b, i) => {
                    const day = new Date(b.date).getDate();

                    return (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between mb-3 p-2 border rounded"
                      >
                        {/* LEFT */}
                        <div className="d-flex align-items-center gap-3">

                          {/* DATE BOX */}
                          <div
                            style={{
                              background: "#eee",
                              borderRadius: "10px",
                              width: "55px",
                              height: "55px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "18px"
                            }}
                          >
                            {day}
                          </div>

                          {/* SERVICE + TIME */}
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {b.service}
                            </div>
                            <small className="text-muted">
                              {b.time}
                            </small>
                          </div>

                        </div>

                        {/* RIGHT TIMER */}
                        <div style={{ fontWeight: 600, color: "#0d6efd" }}>
                          ⏳ {getLiveCountdown(b.date, b.time)}
                        </div>

                      </div>
                    );
                  })
                )}
              </CardBody>
            </Card>
          </Col>

        </Row>

      </Container>
      <Modal isOpen={modal} toggle={() => setModal(!modal)} size="lg">
        <ModalHeader toggle={() => setModal(!modal)}>
          Create Spa Booking
        </ModalHeader>

        <ModalBody>
          <Form>

            {/* SERVICE */}
            <div className="mb-3">
              <Label>Select Service</Label>
              <Input
                type="select"
                onChange={(e) => handleServiceChange(e.target.value)}
              >
                <option value="">Select</option>
                {services.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.name} - ₹{s.price}
                  </option>
                ))}
              </Input>
            </div>

            {/* NAME + ROOM + MOBILE */}
            <Row>
              <Col md="4">
                <Input
                  placeholder="Name"
                  name="name"
                  onChange={handleChange}
                />
              </Col>
              <Col md="4">
                <Input
                  placeholder="Room"
                  name="roomNumber"
                  onChange={handleChange}
                />
              </Col>
              <Col md="4">
                <Input
                  placeholder="Mobile"
                  name="mobileNumber"
                  onChange={handleChange}
                />
              </Col>
            </Row>

            {/* DURATION + PRICE */}
            <Row className="mt-3">
              <Col md="6">
                <Input value={form.duration} disabled placeholder="Duration" />
              </Col>
              <Col md="6">
                <Input value={form.price} disabled placeholder="Price" />
              </Col>
            </Row>

            {/* DATE + TIME */}
            <Row className="mt-3">
              <Col md="6">
                <Input type="date" name="date" onChange={handleChange} />
              </Col>
              <Col md="6">
                <Input type="time" name="time" onChange={handleChange} />
              </Col>
            </Row>

            {/* THERAPIST */}
            <div className="mt-3">
              <Label>Therapist Preference</Label>
              <Input
                type="select"
                name="therapist"
                onChange={handleChange}
              >
                <option value="no-pref">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </Input>
            </div>

            {/* ADDONS */}
            <div className="mt-3">
              <Label>Add-ons</Label>
              <div className="d-flex gap-3 flex-wrap">
                {["steam", "jacuzzi", "herbalTea", "aromaticOils"].map(a => (
                  <label key={a}>
                    <input
                      type="checkbox"
                      name={a}
                      onChange={handleAddonChange}
                    /> {a}
                  </label>
                ))}
              </div>
            </div>

            {/* NOTES */}
            <div className="mt-3">
              <Label>Special Request</Label>
              <Input
                type="textarea"
                name="notes"
                onChange={handleChange}
              />
            </div>

          </Form>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Dashboard;